export interface AbsenceReasonDefinition {
  code: string;
  absenceType: string | null;
  label: string;
  coverageOnly?: boolean;
}

/** Unified absence reasons for Time Records and Assign Coverage. */
export const ABSENCE_REASON_DEFINITIONS: readonly AbsenceReasonDefinition[] = [
  { code: 'NO_CALL_NO_SHOW', absenceType: 'no_show', label: 'No Call / No show' },
  { code: 'SICK', absenceType: 'sick', label: 'Sick' },
  { code: 'PTO', absenceType: 'vacation', label: 'Vacation' },
  { code: 'DAY_OFF', absenceType: 'other', label: 'Day Off' },
  { code: 'PERSONAL', absenceType: 'personal', label: 'Personal Day' },
  { code: 'UNEXCUSED', absenceType: 'unexcused', label: 'Unexcused' },
  { code: 'HOLIDAY', absenceType: 'holiday', label: 'Holiday' },
  { code: 'BEREAVEMENT', absenceType: 'bereavement', label: 'Bereavement' },
  { code: 'MATERNITY', absenceType: 'maternity', label: 'Maternity' },
  { code: 'JURY_DUTY', absenceType: 'jury_duty', label: 'Jury Duty' },
  { code: 'EMERGENCY', absenceType: 'other', label: 'Emergency' },
  { code: 'TRAINING', absenceType: 'training', label: 'Training' },
  { code: 'SUSPENSION', absenceType: 'suspension', label: 'Suspension' },
  { code: 'OTHER', absenceType: 'other', label: 'Other' },
  {
    code: 'PENDING_SUPERVISOR_REVIEW',
    absenceType: null,
    label: 'Pending Supervisor Review',
    coverageOnly: true,
  },
];

export const ABSENCE_REASON_OPTIONS = ABSENCE_REASON_DEFINITIONS.map((d) => ({
  code: d.code,
  label: d.label,
}));

/** Reasons that create an approved absence record (excludes pending-review placeholder). */
export const RECORDABLE_ABSENCE_REASON_OPTIONS = ABSENCE_REASON_DEFINITIONS.filter(
  (d) => d.absenceType && d.code !== 'PENDING_SUPERVISOR_REVIEW',
).map(({ code, label }) => ({ code, label }));

/** Options for Time Records / absence records (no pending-review placeholder). */
export const ABSENCE_TYPE_OPTIONS = (() => {
  const byType = new Map<string, string>();
  for (const def of ABSENCE_REASON_DEFINITIONS) {
    if (def.absenceType) byType.set(def.absenceType, def.label);
  }
  return Array.from(byType, ([value, label]) => ({ value, label }));
})();

export function getAbsenceTypeLabel(absenceType: string, reason?: string | null): string {
  return getApprovedAbsenceDisplayLabel({ absenceType, reason });
}

function resolveGenericAbsenceTypeLabel(absenceType: string, reason?: string | null): string {
  const trimmedReason = reason?.trim() || '';
  if (trimmedReason) {
    const byReason = ABSENCE_REASON_DEFINITIONS.find((d) => d.code === trimmedReason);
    if (byReason) return byReason.label;
    if (absenceType === 'other') return 'Other';
  }
  if (absenceType === 'other') {
    return 'Other';
  }
  const def = ABSENCE_REASON_DEFINITIONS.find((d) => d.absenceType === absenceType);
  if (def) return def.label;
  return absenceType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatAbsenceReasonLabel(reason?: string | null): string {
  if (!reason) return 'Pending Supervisor Review';
  const def = ABSENCE_REASON_DEFINITIONS.find((d) => d.code === reason);
  if (def) return def.label;
  if (reason === 'FAMILY') return 'Family';
  if (reason === 'APPROVED_LEAVE') return 'Approved Leave';
  return reason.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function isTimeOffSyncReason(reason?: string | null): boolean {
  return Boolean(reason?.startsWith('time_off_request:'));
}

export function isKnownAbsenceReasonCode(reason?: string | null): boolean {
  const trimmed = reason?.trim() || '';
  if (!trimmed) return false;
  if (isTimeOffSyncReason(trimmed)) return true;
  return ABSENCE_REASON_DEFINITIONS.some((d) => d.code === trimmed);
}

/** Free-text supervisor note on an absence (not a reason code like DAY_OFF or OTHER). */
export function getAbsenceNoteText(reason?: string | null): string | null {
  const trimmed = reason?.trim() || '';
  if (!trimmed || isKnownAbsenceReasonCode(trimmed)) return null;
  return trimmed;
}

const NON_DAY_OFF_OTHER_REASON_CODES = new Set(
  ABSENCE_REASON_DEFINITIONS
    .filter((def) => def.absenceType === 'other' && def.code !== 'DAY_OFF')
    .map((def) => def.code),
);

/** Approved PTO / vacation from time-off sync — not schedule day off with free-text reason. */
export function isApprovedVacationAbsence(input: {
  absenceType?: string | null;
  reason?: string | null;
}): boolean {
  const reason = input.reason?.trim() || '';
  if (input.absenceType !== 'vacation') return false;
  if (!reason || reason === 'DAY_OFF') return false;
  if (isTimeOffSyncReason(reason)) return true;
  return reason === 'PTO';
}

/** Schedule day off — only when explicitly DAY_OFF or legacy empty other/vacation rows. */
export function isManualDayOffAbsence(input: {
  absenceType?: string | null;
  reason?: string | null;
}): boolean {
  const reason = input.reason?.trim() || '';
  if (reason === 'DAY_OFF') return true;
  if (isTimeOffSyncReason(reason)) return false;
  if (isApprovedVacationAbsence(input)) return false;

  if (input.absenceType === 'other') {
    if (reason === 'OTHER' || NON_DAY_OFF_OTHER_REASON_CODES.has(reason)) return false;
    // Free-text reason = supervisor "Other" absence with notes — not day off.
    if (reason) return false;
    // Empty reason: legacy schedule day off row.
    return true;
  }

  if (input.absenceType === 'vacation') {
    if (!reason || reason === 'DAY_OFF') return true;
    if (!isApprovedVacationAbsence(input)) return true;
  }

  return false;
}

/** Company / schedule holiday — identify on the day row, do not count as payroll absence. */
export function isHolidayAbsence(input: {
  absenceType?: string | null;
  reason?: string | null;
}): boolean {
  const reason = input.reason?.trim() || '';
  return input.absenceType === 'holiday' || reason === 'HOLIDAY';
}

/**
 * Absences that still appear under Abs Types on the time sheet even when they do not
 * count toward Abs Days / Abs Hrs (currently Holiday only).
 */
export function isDisplayOnlyAbsenceForTimeSheet(input: {
  absenceType?: string | null;
  reason?: string | null;
}): boolean {
  return isHolidayAbsence(input);
}

/** Approved absences that count toward payroll time-sheet absence days/hours (excludes day off, vacation/PTO, and holiday). */
export function isCountableAbsenceForTimeSheet(input: {
  absenceType?: string | null;
  reason?: string | null;
}): boolean {
  return (
    !isManualDayOffAbsence(input)
    && !isApprovedVacationAbsence(input)
    && !isHolidayAbsence(input)
  );
}

export function resolveScheduleAbsenceReason(status: string, reason?: string | null): string | undefined {
  if (status === 'day_off') return 'DAY_OFF';
  if (status === 'vacation') return 'PTO';
  const trimmed = reason?.trim();
  return trimmed || undefined;
}

export function getApprovedAbsenceDisplayLabel(input: {
  absenceType?: string | null;
  reason?: string | null;
}): string {
  if (isManualDayOffAbsence(input)) return 'Day Off';
  if (input.absenceType === 'personal' || input.reason === 'PERSONAL') return 'Personal Day';
  if (isApprovedVacationAbsence(input)) return 'Vacation';
  if (isHolidayAbsence(input)) return 'Holiday';
  return resolveGenericAbsenceTypeLabel(input.absenceType || 'other', input.reason);
}

/** Short label for Missing Agents chip (e.g. "Personal Day / Approved"). */
export function getApprovedAbsenceChipLabel(input: {
  absenceType?: string | null;
  reason?: string | null;
}): string {
  if (isManualDayOffAbsence(input)) return 'Day Off';
  if (input.absenceType === 'personal' || input.reason === 'PERSONAL') return 'Personal Day';
  if (isApprovedVacationAbsence(input)) return 'Vacation';
  if (isHolidayAbsence(input)) return 'Holiday';
  if (input.absenceType === 'other' && !isManualDayOffAbsence(input)) return 'Other';
  return getApprovedAbsenceDisplayLabel(input);
}
